import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, MessageCircle, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { BRAND, UNITS } from "../../data/project";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    typology: "",
    message: "",
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const buildMessage = () => {
    const t = form.typology || "Any typology";
    return `Hello ${BRAND.agent},%0A%0AI'd like to register interest in Hudayriyat Golf Estates.%0A%0AName: ${form.name}%0AEmail: ${form.email}%0APhone: ${form.phone}%0ATypology of interest: ${t}%0A%0A${form.message}`;
  };

  const validate = () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please complete name, email and phone before submitting.");
      return false;
    }
    return true;
  };

  const sendWhatsApp = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const url = `https://wa.me/${BRAND.phoneRaw}?text=${buildMessage()}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp. Carlton will be in touch shortly.");
  };

  const sendEmail = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const subject = encodeURIComponent(
      "Hudayriyat Golf Estates: Expression of Interest"
    );
    const body = decodeURIComponent(buildMessage().replace(/%0A/g, "\n"));
    window.location.href = `mailto:${BRAND.email}?subject=${subject}&body=${encodeURIComponent(body)}`;
    toast.success("Drafted email to PK Properties.");
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#F4F2EC] py-24 md:py-36 border-t hairline"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-10 md:gap-14">
          {/* Left: pitch */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="col-span-12 md:col-span-5"
          >
            <div className="overline mb-5">07 / Register Interest</div>
            <h2
              className="font-serif leading-[1.02] tracking-tight text-[#1A3626]"
              style={{ fontSize: "clamp(40px, 5vw, 72px)" }}
            >
              Reserve your
              <br />
              <span className="italic font-light">consultation.</span>
            </h2>
            <p className="mt-6 text-[#1A3626]/75 font-light leading-relaxed text-base md:text-lg max-w-md pr-4">
              Allocations are made privately. Share your details and Carlton
              will personally walk you through unit availability, floor
              orientations and the booking process.
            </p>

            <div className="mt-12 space-y-1 pt-8 border-t hairline">
              <div className="overline mb-4">Direct Line</div>
              <a
                href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
                data-testid="contact-direct-phone"
                className="flex items-center gap-3 py-3 text-[#1A3626] hover:text-[#B3906A] transition-colors group"
              >
                <Phone className="w-4 h-4" strokeWidth={1.5} />
                <span className="font-serif text-2xl">{BRAND.phone}</span>
              </a>
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="contact-direct-email"
                className="flex items-center gap-3 py-3 text-[#1A3626] hover:text-[#B3906A] transition-colors"
              >
                <Mail className="w-4 h-4" strokeWidth={1.5} />
                <span className="font-serif text-xl">{BRAND.email}</span>
              </a>
              <a
                href={`https://wa.me/${BRAND.phoneRaw}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-direct-whatsapp"
                className="flex items-center gap-3 py-3 text-[#1A3626] hover:text-[#B3906A] transition-colors"
              >
                <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                <span className="font-serif text-xl">
                  WhatsApp {BRAND.agent}
                </span>
              </a>
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="col-span-12 md:col-span-7 bg-white p-7 md:p-12 border hairline"
            data-testid="contact-form"
            onSubmit={sendWhatsApp}
            noValidate
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
              <Field
                label="Full Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                testid="form-name"
                required
              />
              <Field
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                testid="form-email"
                required
              />
              <Field
                label="Phone (with country code)"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                testid="form-phone"
                required
              />
              <div className="pt-7">
                <label className="overline block mb-3" style={{ color: "#1A3626", opacity: 0.55 }}>
                  Typology of Interest
                </label>
                <select
                  name="typology"
                  value={form.typology}
                  onChange={handleChange}
                  data-testid="form-typology"
                  className="hge-input"
                >
                  <option value="">Select a typology</option>
                  {UNITS.map((u) => (
                    <option key={u.id} value={u.name}>
                      {u.name}, {u.priceLabel}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="pt-8 mt-2">
              <label
                className="overline block mb-3"
                style={{ color: "#1A3626", opacity: 0.55 }}
              >
                Message
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                data-testid="form-message"
                placeholder="Tell us about your investment horizon, preferred view, etc."
                className="hge-textarea"
              />
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                data-testid="contact-submit-whatsapp"
                className="hge-btn-primary flex-1 justify-center"
              >
                <MessageCircle className="w-4 h-4" />
                Send via WhatsApp
                <ArrowUpRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={sendEmail}
                data-testid="contact-submit-email"
                className="hge-btn-ghost flex-1 justify-center"
              >
                <Mail className="w-4 h-4" />
                Send via Email
              </button>
            </div>

            <p className="mt-5 text-[11px] text-[#6A736E] font-light leading-relaxed">
              By submitting you agree to be contacted by {BRAND.agency}
              regarding this development. Your details are not shared with any
              third party.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, type = "text", testid, required }) {
  return (
    <div className="pt-7">
      <label
        htmlFor={name}
        className="overline block mb-3"
        style={{ color: "#1A3626", opacity: 0.55 }}
      >
        {label} {required && <span className="text-[#B3906A]">*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="hge-input"
        required={required}
      />
    </div>
  );
}
